<template>
  <div class="article-actions">
    <div class="header">
      <div class="logo">
        <img :src="agro" alt="Логотип Я в агро" />
      </div>
      <div class="info">
        <span class="date">{{ toMonthYearDateFormat(article.publicationDate) }}</span>
        <span class="author">{{ article.author_name || 'Редакция «Я в Агро»' }}</span>
      </div>
    </div>

    <div v-if="article.is_show_comments" class="button-wrapper">
      <BaseButton
        name="Оставить комментарий"
        theme="potato"
        theme-type="outline"
        size="xl"
        icon-left="messages/message"
        @click="onClickComment"
      />
      <span class="hint">{{ article.comments.length }}</span>
    </div>

    <div class="share">
      <button class="button-share" @click="shareToVK">
        <BaseIconSvoi icon="svoe/vk" size="l" />
      </button>
      <button class="button-share" @click="shareToTelegram">
        <BaseIconSvoi icon="svoe/telegram" size="l" />
      </button>
      <button class="button-share" @click="copyLink">
        <BaseIconSvoi icon="essential/share-2" size="l" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { BaseButton } from '~/components/base-ui/buttons'
  import type { Article } from '~/models/articles'
  import { useNotificationsStore } from '~/store/notifications'
  import { toMonthYearDateFormat } from '~/shared/utils/date'
  import agro from '~/assets/images/logotypes/agro.svg'

  const route = useRoute()

  const props = defineProps<{
    article: Article
  }>()

  const emit = defineEmits<{
    (e: 'scroll-to-comments'): void
  }>()

  const { isAuth } = useAuth()
  const { openModal } = useAuthModal()
  const notificationsStore = useNotificationsStore()

  const getShareUrl = () => {
    if (!import.meta.client) return ''
    return window.location.href
  }

  const shareToVK = () => {
    const url = encodeURIComponent(getShareUrl())
    const title = encodeURIComponent(props.article.name)
    window.open(
      `https://vk.com/share.php?url=${url}&title=${title}`,
      '_blank',
      'width=600,height=400'
    )
  }

  const shareToTelegram = () => {
    const url = encodeURIComponent(getShareUrl())
    const title = encodeURIComponent(props.article.name)
    window.open(`https://t.me/share/url?url=${url}&text=${title}`, '_blank', 'width=600,height=400')
  }

  const onClickComment = () => {
    if (!isAuth.value) {
      openModal({ backUrl: `${route.path}#comments` })
      return
    }
    emit('scroll-to-comments')
  }

  const copyLink = async () => {
    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(getShareUrl())
        notificationsStore.pushInfo('Данные успешно скопированы в буфер обмена')
      } catch {
        notificationsStore.pushError('Не удалось скопировать ссылку')
      }
    }
  }
</script>

<style lang="scss" scoped>
  .article-actions {
    @extend %flex-column;

    gap: 16px;
    padding: 24px;
    border-radius: 24px;
    background-color: $color-default-white;

    @include vp-767 {
      padding: 20px;
    }

    > .header {
      display: flex;
      align-items: center;
      gap: 16px;

      > .logo > img {
        width: 60px;
        height: 60px;
        border-radius: 50%;
      }

      > .info {
        @extend %flex-column;

        gap: 4px;

        > .date {
          font-size: 16px;
          line-height: 24px;
          color: $color-potato-60;
        }

        > .author {
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
        }
      }
    }

    > .button-wrapper {
      position: relative;
      width: 100%;

      :deep(.svoi-button) {
        width: 100%;

        > .name {
          text-align: left;
        }
      }

      > .hint {
        position: absolute;
        top: -10px;
        right: 8px;
        display: inline-block;
        padding: 2px 8px;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        color: $color-default-white;
        border-radius: 50px;
        background-color: $color-banana-70;
      }
    }

    > .share {
      display: flex;
      gap: 16px;
      justify-content: center;

      > .button-share {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 48px;
        height: 48px;
        color: $color-kiwi-50;
        border-radius: 50px;
        background: $color-kiwi-10;
        border: none;
        cursor: pointer;
        transition: 0.2s ease-in-out;

        &:hover {
          color: $color-default-white;
          background-color: $color-kiwi-50;
        }
      }
    }
  }
</style>
